import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  GridApi,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';

import useGeneratikTableStore from '@/shared/ui/GeneratikTable/generatikTableStore';

export interface UseTableSelectionParams<R extends GridValidRowModel> {
  rows: R[];
  apiRef: MutableRefObject<GridApi>;
  getRowId?: (row: R) => string | number;
  radioSelection?: boolean;
  paginationMode?: 'client' | 'server';
  selectDescendants?: boolean;
  treeData?: boolean;
  getTreeDataPath?: (row: R) => string[];
  externalSelectionModel?: GridRowSelectionModel;
  clearSelectionKey?: unknown;
  groupByField?: string | null;
  /**
   * Rows this rejects take no part in ancestor reconciliation. An ancestor is selected only when
   * every descendant is, so a descendant that can never be selected would otherwise make its
   * whole branch impossible to tick.
   */
  isRowSelectable?: (params: GridRowParams<R>) => boolean;
  /**
   * Rows this accepts stay out of tree selection propagation entirely: ticking an ancestor
   * leaves them untouched, and their own selection never ticks nor blocks an ancestor. They
   * remain individually selectable.
   */
  isRowSelectionIndependent?: (row: R) => boolean;
  persistSelectedRows?: boolean;
  headersRequest?: { pageType: string; resourceId?: string };
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  onSelectedRowsChange?: (rows: R[]) => void;
}

export interface UseTableSelectionReturn<R extends GridValidRowModel> {
  selectionModel: GridRowSelectionModel;
  visibleSelectionModel: GridRowSelectionModel;
  handleRowSelectionChange: (model: GridRowSelectionModel) => void;
  toggleRowSelection: (rowId: GridRowId) => void;
  resolveRowId: (row: R) => string | number;
  currentRowIds: Set<string>;
}

const defaultGetRowId = <R extends GridValidRowModel>(row: R) =>
  (row as unknown as { id: string | number }).id;

const AUTO_GENERATED_PREFIX = 'auto-generated-row-';

const buildSyntheticGroupId = (key: string | number): string =>
  `${AUTO_GENERATED_PREFIX}null/${key}`;

const isSyntheticGroupId = (id: unknown): boolean =>
  typeof id === 'string' && id.startsWith(AUTO_GENERATED_PREFIX);

// `columns` is required by GridRowParams but no selectability predicate reads it.
const toRowParams = <R extends GridValidRowModel>(id: GridRowId, row: R): GridRowParams<R> => ({
  id,
  row,
  columns: [],
});

function buildDescendantsByAncestor<R extends GridValidRowModel>(
  rows: R[],
  getTreeDataPath: (row: R) => string[],
  resolveRowId: (row: R) => string | number,
  groupByField: string | null | undefined,
  isRowSelectable: ((params: GridRowParams<R>) => boolean) | undefined,
  isRowSelectionIndependent?: (row: R) => boolean,
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  const pushDescendant = (ancestorId: string, descendantId: string) => {
    const existing = map.get(ancestorId);
    if (existing) existing.add(descendantId);
    else map.set(ancestorId, new Set([descendantId]));
  };

  for (const row of rows) {
    if (isRowSelectionIndependent?.(row)) continue;
    const rowId = resolveRowId(row);
    if (isRowSelectable && !isRowSelectable(toRowParams(rowId, row))) continue;
    const path = getTreeDataPath(row);
    if (path.length <= 1) continue;
    const descendantId = String(rowId);
    for (let depth = path.length - 2; depth >= 0; depth -= 1) {
      const isSyntheticAncestor = depth === 0 && !!groupByField;
      const ancestorId = isSyntheticAncestor
        ? buildSyntheticGroupId(path[depth])
        : String(path[depth]);
      pushDescendant(ancestorId, descendantId);
    }
  }
  return map;
}

function reconcileAncestors(
  next: Set<string>,
  descendantsByAncestor: Map<string, Set<string>>,
): void {
  const ancestorsByDepth = [...descendantsByAncestor.entries()].sort(
    (a, b) => a[1].size - b[1].size,
  );
  for (const [ancestorId, descendants] of ancestorsByDepth) {
    const selectedCount = [...descendants].filter((d) => next.has(d)).length;
    if (selectedCount === descendants.size) {
      next.add(ancestorId);
    } else {
      next.delete(ancestorId);
    }
  }
}

function finalizeSelectionModel<R extends GridValidRowModel>(
  next: Set<string>,
  rows: R[],
  resolveRowId: (row: R) => string | number,
): GridRowSelectionModel {
  const dataIdsInOrder = rows.map((row) => String(resolveRowId(row))).filter((id) => next.has(id));
  const syntheticIds = [...next].filter(isSyntheticGroupId);
  const accountedFor = new Set([...syntheticIds, ...dataIdsInOrder]);
  const unmatchedIds = [...next].filter((id) => !accountedFor.has(id));
  return [...syntheticIds, ...dataIdsInOrder, ...unmatchedIds] as GridRowSelectionModel;
}

function applyTreeDescendantSelection<R extends GridValidRowModel>(
  previousModel: GridRowSelectionModel,
  proposedModel: GridRowSelectionModel,
  rows: R[],
  getTreeDataPath: (row: R) => string[],
  resolveRowId: (row: R) => string | number,
  groupByField: string | null | undefined,
  isRowSelectable: ((params: GridRowParams<R>) => boolean) | undefined,
  isRowSelectionIndependent?: (row: R) => boolean,
): GridRowSelectionModel {
  const proposed = new Set(proposedModel.map(String));
  const previous = new Set(previousModel.map(String));
  const descendantsByAncestor = buildDescendantsByAncestor(
    rows,
    getTreeDataPath,
    resolveRowId,
    groupByField,
    isRowSelectable,
    isRowSelectionIndependent,
  );

  const added = [...proposed].filter((id) => !previous.has(id));
  const removed = [...previous].filter((id) => !proposed.has(id));

  const next = new Set(proposed);

  for (const id of added) {
    const descendants = descendantsByAncestor.get(id);
    if (descendants) for (const d of descendants) next.add(d);
  }

  for (const id of removed) {
    const descendants = descendantsByAncestor.get(id);
    if (descendants) for (const d of descendants) next.delete(d);
  }

  reconcileAncestors(next, descendantsByAncestor);

  return finalizeSelectionModel(next, rows, resolveRowId);
}

function reconcileExternalSelection<R extends GridValidRowModel>(
  externalModel: GridRowSelectionModel,
  rows: R[],
  getTreeDataPath: (row: R) => string[],
  resolveRowId: (row: R) => string | number,
  groupByField: string | null | undefined,
  isRowSelectionIndependent?: (row: R) => boolean,
): GridRowSelectionModel {
  const next = new Set(externalModel.map(String));
  const descendantsByAncestor = buildDescendantsByAncestor(
    rows,
    getTreeDataPath,
    resolveRowId,
    groupByField,
    undefined,
    isRowSelectionIndependent,
  );
  reconcileAncestors(next, descendantsByAncestor);
  return finalizeSelectionModel(next, rows, resolveRowId);
}

function modelsEqual(a: GridRowSelectionModel, b: GridRowSelectionModel): boolean {
  return a.length === b.length && a.every((id, i) => id === b[i]);
}

const useTableSelection = <R extends GridValidRowModel>({
  rows,
  apiRef,
  getRowId,
  radioSelection = false,
  paginationMode = 'client',
  selectDescendants = false,
  treeData = false,
  getTreeDataPath,
  externalSelectionModel,
  clearSelectionKey,
  groupByField,
  isRowSelectable,
  isRowSelectionIndependent,
  persistSelectedRows = false,
  headersRequest,
  onRowSelectionModelChange,
  onSelectedRowsChange,
}: UseTableSelectionParams<R>): UseTableSelectionReturn<R> => {
  const resolveRowId = useMemo<(row: R) => string | number>(
    () => getRowId ?? defaultGetRowId,
    [getRowId],
  );

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => {
    if (externalSelectionModel !== undefined) return externalSelectionModel;
    if (!persistSelectedRows || !headersRequest?.pageType) return [];
    const storedRows =
      useGeneratikTableStore.getState().selectedRows[headersRequest.pageType] ?? [];
    return storedRows.map((row) => resolveRowId(row as R));
  });

  useEffect(() => {
    if (externalSelectionModel === undefined) return;
    const reconciled =
      treeData && getTreeDataPath && (selectDescendants || groupByField)
        ? reconcileExternalSelection(
            externalSelectionModel,
            rows,
            getTreeDataPath,
            resolveRowId,
            groupByField,
            isRowSelectionIndependent,
          )
        : externalSelectionModel;
    setSelectionModel((prev) => (modelsEqual(prev, reconciled) ? prev : reconciled));
  }, [
    externalSelectionModel,
    treeData,
    getTreeDataPath,
    selectDescendants,
    groupByField,
    rows,
    resolveRowId,
    isRowSelectionIndependent,
  ]);

  const onSelectedRowsChangeRef = useRef(onSelectedRowsChange);
  onSelectedRowsChangeRef.current = onSelectedRowsChange;
  const onRowSelectionModelChangeRef = useRef(onRowSelectionModelChange);
  onRowSelectionModelChangeRef.current = onRowSelectionModelChange;
  const headersRequestRef = useRef(headersRequest);
  headersRequestRef.current = headersRequest;

  const resetSelection = useCallback(() => {
    setSelectionModel([]);
    onSelectedRowsChangeRef.current?.([]);
    if (persistSelectedRows && headersRequestRef.current?.pageType) {
      useGeneratikTableStore.getState().setSelectedRows(headersRequestRef.current.pageType, []);
    }
  }, [persistSelectedRows]);

  const isFirstClearKeyRender = useRef(true);
  useEffect(() => {
    if (isFirstClearKeyRender.current) {
      isFirstClearKeyRender.current = false;
      return;
    }
    resetSelection();
  }, [clearSelectionKey, resetSelection]);

  const currentRowIds = useMemo(
    () => new Set(rows.map((row) => String(resolveRowId(row)))),
    [rows, resolveRowId],
  );

  // When rows change (e.g. after deletion), remove stale selected IDs from the
  // internal model and notify parent callbacks so selection-dependent UI updates.
  // Skip when externalSelectionModel is used — the parent controls selection directly.
  // Skip in server pagination — IDs from other pages are valid even if absent from currentRowIds.
  useEffect(() => {
    if (externalSelectionModel !== undefined) return;
    if (paginationMode === 'server') return;

    setSelectionModel((prev) => {
      if (prev.length === 0) return prev;

      const validIds = prev.filter((id) => currentRowIds.has(String(id)) || isSyntheticGroupId(id));

      if (validIds.length === prev.length) return prev;

      onRowSelectionModelChangeRef.current?.(validIds);

      const selectedIds = new Set(validIds.map(String));
      const newSelectedRows = rows.filter((row) => selectedIds.has(String(resolveRowId(row))));
      onSelectedRowsChangeRef.current?.(newSelectedRows);

      if (persistSelectedRows && headersRequestRef.current?.pageType) {
        useGeneratikTableStore
          .getState()
          .setSelectedRows(headersRequestRef.current.pageType, newSelectedRows);
      }

      return validIds;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentRowIds already captures rows+resolveRowId
  }, [currentRowIds, externalSelectionModel, paginationMode, persistSelectedRows]);

  const visibleSelectionModel = useMemo(
    () =>
      selectionModel.filter(
        (id) =>
          currentRowIds.has(String(id)) ||
          isSyntheticGroupId(id) ||
          apiRef.current?.getRowNode?.(id)?.type === 'group',
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable ref object
    [selectionModel, currentRowIds],
  );

  const handleRowSelectionChange = useCallback(
    (newSelectionModel: GridRowSelectionModel) => {
      let effective: GridRowSelectionModel;

      if (radioSelection) {
        effective = newSelectionModel.filter((id) => !selectionModel.includes(id)).slice(-1);
      } else if (paginationMode === 'server') {
        const otherPageIds = selectionModel.filter(
          (id) =>
            !currentRowIds.has(String(id)) && apiRef.current?.getRowNode(id)?.type !== 'group',
        );
        effective = [...new Set([...otherPageIds, ...newSelectionModel])];
      } else {
        effective = newSelectionModel;
      }

      if (treeData && getTreeDataPath && (selectDescendants || groupByField)) {
        effective = applyTreeDescendantSelection(
          selectionModel,
          effective,
          rows,
          getTreeDataPath,
          resolveRowId,
          groupByField,
          isRowSelectable,
          isRowSelectionIndependent,
        );
      }

      if (modelsEqual(effective, selectionModel)) return;

      setSelectionModel(effective);
      onRowSelectionModelChange?.(effective);

      const selectedIds = new Set(effective.map(String));
      const newSelectedRows = rows.filter((row) => selectedIds.has(String(resolveRowId(row))));
      onSelectedRowsChange?.(newSelectedRows);

      if (persistSelectedRows && headersRequest?.pageType) {
        useGeneratikTableStore.getState().setSelectedRows(headersRequest.pageType, newSelectedRows);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apiRef is a stable ref object
    [
      radioSelection,
      selectionModel,
      paginationMode,
      currentRowIds,
      selectDescendants,
      treeData,
      getTreeDataPath,
      groupByField,
      isRowSelectable,
      isRowSelectionIndependent,
      rows,
      resolveRowId,
      onRowSelectionModelChange,
      onSelectedRowsChange,
      persistSelectedRows,
      headersRequest?.pageType,
    ],
  );

  // A disabled checkbox is pointer-events: none, so its click lands on the cell and reaches this
  // toggle without ever meeting the guard the checkbox itself enforced. Ids the table does not
  // hold (other pages, synthetic groups) carry no row to judge and pass through.
  const isRowIdSelectable = useCallback(
    (rowId: GridRowId) => {
      if (!isRowSelectable) return true;
      const row = rows.find((candidate) => String(resolveRowId(candidate)) === String(rowId));
      return !row || isRowSelectable(toRowParams(rowId, row));
    },
    [isRowSelectable, rows, resolveRowId],
  );

  const toggleRowSelection = useCallback(
    (rowId: GridRowId) => {
      if (!isRowIdSelectable(rowId)) return;
      if (radioSelection) {
        handleRowSelectionChange(selectionModel.includes(rowId) ? [] : [rowId]);
        return;
      }
      const next = selectionModel.includes(rowId)
        ? selectionModel.filter((id) => id !== rowId)
        : [...selectionModel, rowId];
      handleRowSelectionChange(next);
    },
    [isRowIdSelectable, radioSelection, selectionModel, handleRowSelectionChange],
  );

  return {
    selectionModel,
    visibleSelectionModel,
    handleRowSelectionChange,
    toggleRowSelection,
    resolveRowId,
    currentRowIds,
  };
};

export default useTableSelection;
